// Mercapoli - Login Page
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff, Loader2, ArrowLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, user, error, clearError } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Redirigir si ya está autenticado
  useEffect(() => {
    if (isAuthenticated && user) {
      const from = location.state?.from?.pathname;
      const redirectMap = {
        customer: '/app',
        delivery: '/delivery',
        restaurant: '/restaurant',
        admin: '/admin'
      };
      navigate(from || redirectMap[user.rol] || '/app');
    }
  }, [isAuthenticated, user, navigate, location]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      clearError();
    }
  }, [error, clearError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const userData = await login(formData.email, formData.password);
      toast.success(`¡Bienvenido, ${userData.nombre}!`);
    } catch (err) {
      // Error ya manejado en el context
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  // Quick login buttons for demo
  const quickLogins = [
    { rol: 'Admin', email: 'admin@mercapoli.com', password: 'admin123' },
  ];

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <Link 
            to="/" 
            className="inline-flex items-center text-muted-foreground hover:text-foreground mb-8"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver al inicio
          </Link>

          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-spicy-coral to-[#FF8E53] flex items-center justify-center">
                <span className="text-white font-bold text-xl">M</span>
              </div>
              <span className="font-heading text-xl font-bold">Mercapoli</span>
            </div>
            <h1 className="font-heading text-3xl font-bold mb-2">Bienvenido de nuevo</h1>
            <p className="text-muted-foreground">
              Ingresa tus credenciales para acceder a tu cuenta
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Correo electrónico</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="tu@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  className="pl-10"
                  required
                  data-testid="login-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between">
                <Label htmlFor="password">Contraseña</Label>
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-spicy-coral hover:underline"
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  className="pl-10 pr-10"
                  required
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Forgot Password Link */}
            <div className="text-right">
              <Link to="/recuperar-password" className="text-sm text-primary hover:underline">
                ¿Olvidaste tu contraseña?
              </Link>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-spicy-coral hover:bg-spicy-coral/90 py-6"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Iniciando sesión...
                </>
              ) : (
                'Iniciar sesión'
              )}
            </Button>
          </form>

          {/* Legal Links (Required for App Store) */}
          <div className="mt-6 pt-4 border-t border-border text-center text-xs text-muted-foreground">
            Al iniciar sesión aceptas nuestros{' '}
            <Link to="/terminos" className="text-primary hover:underline">Términos</Link>
            {' '}y{' '}
            <Link to="/privacidad" className="text-primary hover:underline">Política de Privacidad</Link>
          </div>

          {/* Quick login para demo */}
          <div className="mt-6">
            <p className="text-sm text-center text-muted-foreground mb-3">
              Acceso rápido (demo):
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              {quickLogins.map((item) => (
                <Button
                  key={item.rol}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setFormData({ email: item.email, password: item.password });
                  }}
                  data-testid={`quick-login-${item.rol.toLowerCase()}`}
                >
                  {item.rol}
                </Button>
              ))}
            </div>
          </div>

          <p className="text-center text-muted-foreground mt-8">
            ¿No tienes cuenta?{' '}
            <Link to="/register" className="text-spicy-coral font-medium hover:underline">
              Regístrate aquí
            </Link>
          </p>
        </motion.div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-spicy-coral/90 to-[#FF8E53]/90" />
        <img
          src="https://images.unsplash.com/photo-1574660375248-e4e63763f8f6?w=800"
          alt="Restaurante"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-spicy-coral/80 to-[#FF8E53]/80" />
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-white text-center">
            <h2 className="font-heading text-4xl font-bold mb-4">
              Tu comida favorita te espera
            </h2>
            <p className="text-white/90 text-lg max-w-md">
              Más de 500 restaurantes listos para entregarte la mejor comida de la ciudad
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
